import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Share2, Lock, Mail, Globe, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';

function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    // scroll handled by <ScrollRestoration />
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketplace
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-neutral-400">Last updated: December 2025</p>
          </div>

          {/* Introduction */}
          <section className="mb-12">
            <p className="text-neutral-300 leading-relaxed mb-4">
              At theailedger.ai (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), we are committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website and use our marketplace platform.
            </p>
            <p className="text-neutral-300 leading-relaxed">
              Please read this privacy policy carefully. By using our platform, you consent to the 
              practices described in this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-white text-black/10 flex items-center justify-center">
                <Database className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Information We Collect</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-[#00F0FF] mb-3">Personal Information</h3>
            <p className="text-neutral-300 mb-4">
              When you register for an account, make a purchase, or become a seller, we may collect:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 mb-6 ml-4">
              <li>Name and email address</li>
              <li>Account credentials (password is encrypted)</li>
              <li>Payment information (processed securely via Stripe)</li>
              <li>Business information for sellers (store name, payout details)</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#00F0FF] mb-3">Automatically Collected Information</h3>
            <p className="text-neutral-300 mb-4">
              When you access our platform, we automatically collect:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li>IP address and browser type</li>
              <li>Device information and operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referral source (how you found us)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h2 className="text-2xl font-bold">How We Use Your Information</h2>
            </div>
            
            <p className="text-neutral-300 mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li>Create and manage your account</li>
              <li>Process transactions and send purchase confirmations</li>
              <li>Facilitate payments between buyers and sellers</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Send important updates about your account or purchases</li>
              <li>Improve our platform and user experience</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
              <li>Send marketing communications (with your consent)</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                <Share2 className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h2 className="text-2xl font-bold">Information Sharing</h2>
            </div>
            
            <p className="text-neutral-300 mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li><strong>Sellers:</strong> When you purchase a product, the seller receives your name and email to deliver the product</li>
              <li><strong>Payment Processors:</strong> Stripe processes all payments securely</li>
              <li><strong>Service Providers:</strong> Third parties that help us operate our platform (hosting, email services)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8" id="cookies">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-white text-black/10 flex items-center justify-center">
                <Cookie className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Cookies & Tracking</h2>
            </div>
            
            <p className="text-neutral-300 mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 mb-6 ml-4">
              <li>Keep you signed in to your account</li>
              <li>Remember your preferences</li>
              <li>Understand how you use our platform</li>
              <li>Improve our services</li>
              <li>Track affiliate referrals</li>
            </ul>
            <p className="text-neutral-300">
              You can control cookies through your browser settings. Note that disabling cookies may 
              affect your ability to use certain features of our platform.
            </p>
          </section>

          {/* Data Security */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h2 className="text-2xl font-bold">Data Security</h2>
            </div>
            
            <p className="text-neutral-300 mb-4">
              We implement appropriate security measures to protect your information:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li>SSL/TLS encryption for all data transmission</li>
              <li>Encrypted password storage</li>
              <li>Secure payment processing through Stripe (PCI compliant)</li>
              <li>Regular security assessments</li>
              <li>Limited employee access to personal data</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h2 className="text-2xl font-bold">Your Rights</h2>
            </div>
            
            <p className="text-neutral-300 mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to processing of your information</li>
              <li>Request data portability</li>
              <li>Withdraw consent for marketing communications</li>
            </ul>
            <p className="text-neutral-300 mt-4">
              To exercise any of these rights, use our self-serve portal —{' '}
              <Link to="/privacy/request-my-data" className="text-[#00F0FF] hover:underline">
                Request my data
              </Link>{' '}
              — or email{' '}
              <a href="mailto:support@theailedger.ai" className="text-[#00F0FF] hover:underline">
                support@theailedger.ai
              </a>. We respond to verified requests within 30 days at no charge.
            </p>
            <p className="text-neutral-300 mt-3 text-sm">
              For our sub-processor list and security controls, see{' '}
              <Link to="/security" className="text-[#00F0FF] hover:underline">/security</Link>.
              A Data Processing Addendum template is available for download from that page.
            </p>
          </section>

          {/* Third-Party Links */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-4">Third-Party Links</h2>
            <p className="text-neutral-300">
              Our platform may contain links to third-party websites or services. We are not responsible 
              for the privacy practices of these external sites. We encourage you to review the privacy 
              policies of any third-party sites you visit.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-4">Children&apos;s Privacy</h2>
            <p className="text-neutral-300">
              Our platform is not intended for children under 18 years of age. We do not knowingly 
              collect personal information from children. If you believe we have collected information 
              from a child, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-4">Changes to This Policy</h2>
            <p className="text-neutral-300">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new policy on this page and updating the &ldquo;Last updated&rdquo; date. We encourage 
              you to review this policy periodically.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-[#00F0FF]" />
              <h2 className="text-2xl font-bold">Contact Us</h2>
            </div>
            <p className="text-neutral-300 mb-4">
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <p className="text-neutral-300">
              <strong>Email:</strong>{' '}
              <a href="mailto:support@theailedger.ai" className="text-[#00F0FF] hover:underline">
                support@theailedger.ai
              </a>
            </p>
            <p className="text-neutral-300 mt-2">
              <strong>Website:</strong>{' '}
              <a href="https://theailedger.ai" className="text-[#00F0FF] hover:underline">
                theailedger.ai
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
